import { Box, Typography, TextField, Chip } from '@mui/material'
import { AutoFixHigh } from '@mui/icons-material'
import { designSpecs } from '../../theme/designSpecs'
import UploadArea from '../common/UploadArea'
import Button from '../common/Button'

const ideaPrompts = [
  {
    label: 'Clean studio background',
    prompt: `Place the product on a clean studio background.
Keep the product exactly the same — do not change its shape, color, material, or details.
Use soft, even studio lighting with realistic shadows.
The result should look like a professional e-commerce product photo.`,
  },
  {
    label: 'Lifestyle flat lay',
    prompt:
      `Create a lifestyle flat-lay image of this product.
Keep the product unchanged.
Place it in a simple, modern setting with soft natural light and subtle shadows.
The final image should look clean, realistic, and suitable for a brand website.`,
  },
  {
    label: 'Premium editorial look',
    prompt:
      `Create a premium, editorial-style product image.
Keep the product exactly the same.
Use an elegant background, soft directional lighting, and realistic shadows.
The image should feel high-end while remaining photorealistic.`,
  },
  {
    label: 'On marble or wood surface',
    prompt:
      `Place the product on a marble or wooden surface.
Keep the product unchanged.
Use soft natural lighting and realistic shadows so the product blends naturally with the surface.
The final image should look suitable for an online store.`,
  },
]

const ControlsPanel = ({
  prompt,
  onPromptChange,
  onFileSelect,
  onGenerate,
  isGenerating,
  hasFile,
  uploadedPreview,
  onDelete,
  onReplace
}) => {
  return (
    <Box
      sx={{
        width: '50%',
        bgcolor: 'white',
        borderRight: '1px solid',
        borderColor: 'grey.200',
        p: 4,
        overflowY: 'auto',
        height: 'calc(100vh - 80px)',
      }}
    >
      <Box sx={{ maxWidth: 500, mx: 'auto' }}>
        {/* Step 1 Indicator */}
        <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'grey.100' }}>
          <Typography
            sx={{
              fontSize: designSpecs.typography.sizes.sm,
              fontWeight: 600,
              color: 'primary.main',
            }}
          >
            Step 1 of 2: Upload & describe
          </Typography>
        </Box>

        {/* Upload Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              color: 'grey.900',
              fontWeight: 600,
            }}
          >
            Upload Your Photo
          </Typography>
          <UploadArea
            onFileSelect={onFileSelect}
            uploadedPreview={uploadedPreview}
            onDelete={onDelete}
            onReplace={onReplace}
          />
        </Box>

        {/* Prompt Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              color: 'grey.900',
              fontWeight: 600,
            }}
          >
            Describe Your Vision
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Describe the background, lighting, or style you want (e.g. “lifestyle shot with plants and soft natural light”)"
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                fontSize: designSpecs.typography.sizes.base,
                borderRadius: designSpecs.borderRadius.lg,
                '& fieldset': {
                  borderWidth: 2,
                  borderColor: 'grey.300',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                  borderWidth: 2,
                },
              },
            }}
          />

          {/* Idea Prompts */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: 'grey.700',
                fontWeight: 500,
                fontSize: designSpecs.typography.sizes.sm,
              }}
            >
              Idea prompts:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ideaPrompts.map((ideaPrompt, index) => (
                <Chip
                  key={index}
                  label={ideaPrompt.label}
                  onClick={() => onPromptChange(ideaPrompt.prompt)}
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'grey.700',
                    fontSize: designSpecs.typography.sizes.sm,
                    cursor: 'pointer',
                    transition: designSpecs.transitions.default,
                    '&:hover': {
                      bgcolor: 'grey.200',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Step 2 Indicator */}
        <Box sx={{ mb: 3, pt: 3, borderTop: '1px solid', borderColor: 'grey.100' }}>
          <Typography
            sx={{
              fontSize: designSpecs.typography.sizes.sm,
              fontWeight: 600,
              color: 'primary.main',
            }}
          >
            Step 2 of 2: Generate & download
          </Typography>
        </Box>

        {/* Generate Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={onGenerate}
          disabled={!hasFile || !prompt.trim() || isGenerating}
          startIcon={<AutoFixHigh />}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            color: '#ffffff',
            py: 2.5,
            fontSize: designSpecs.typography.sizes.lg,
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: designSpecs.borderRadius.xl,
            boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
              boxShadow: '0 6px 20px 0 rgba(99, 102, 241, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            '&.Mui-disabled': {
              opacity: 0.6,
              background: 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)',
              color: '#ffffff',
            },
          }}
        >
          {isGenerating ? 'Generating...' : 'Generate Images'}
        </Button>
      </Box>
    </Box>
  )
}

export default ControlsPanel
